<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  useSlots,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue'
import throttle from 'lodash/throttle'
import HeaderGlobal from '@/components/_core/HeaderGlobal.vue'
import FooterGlobal from '@/components/_core/FooterGlobal.vue'
import MediaQueryObserver from '@/components/_core/MediaQueryObserver.vue'
import TimelineGlobal from '@/components/timeline/TimelineGlobal.vue'
import LoadingScreen from '@/components/loading/LoadingScreen.vue'
import ToastMessage from '@/components/toast/ToastMessage.vue'
import ValidationInspector from '@/components/validation/ValidationInspector.vue'
import ScrollToPageTop from './ScrollToPageTop.vue'
import { useHashLinkHandler } from '@/composables/useHashLinkHandler'
import { useHeaderStore } from '@/stores/header'
import { useScreenLockStore } from '@/stores/screenLock'
import { useUserStore } from '@/stores/user'
import { useClientStore } from '@/stores/client'
import { useMediaQueryStore } from '@/stores/mediaQuery'
import { useChatbotStore } from '@/stores/chatbot'

export default defineComponent({
  name: 'TemplateDefault',

  components: {
    HeaderGlobal,
    FooterGlobal,
    MediaQueryObserver,
    TimelineGlobal,
    LoadingScreen,
    ToastMessage,
    ValidationInspector,
    ScrollToPageTop,
  },

  props: {
    hasHeader: {
      type: Boolean,
      default: true,
    },
    hasFooter: {
      type: Boolean,
      default: true,
    },
    hasGlobalNavigation: {
      type: Boolean,
      default: true,
    },
    hasDrawerNavigation: {
      type: Boolean,
      default: true,
    },
    hasSearchForm: {
      type: Boolean,
      default: true,
    },
    hasTimeline: {
      type: Boolean,
      default: true,
    },
    hasScrollToPageTop: {
      type: Boolean,
      default: true,
    },

    /**
     * コンテンツの全体幅を狭めるかどうか
     */
    isSlim: {
      type: Boolean,
      default: false,
    },

    /**
     * ヘッダーに表示されるロゴの色
     */
    logoColor: {
      type: String as PropType<'default' | 'white' | 'gray'>,
      default: 'default',
    },
  },

  setup(props) {
    // store
    const headerStore = useHeaderStore()
    const screenLockStore = useScreenLockStore()
    const userStore = useUserStore()
    const clientStore = useClientStore()
    const mediaQueryStore = useMediaQueryStore()
    const chatbotStore = useChatbotStore()
    const { registerHashLinkClickHandler } = useHashLinkHandler()

    const asideEl = ref<HTMLElement>()
    const chatbotEl = ref()

    /**
     * メインコンテンツエリアのズレ幅
     *
     * @description
     *  チャットボットがレンダリングされる画面では、チャットボットの幅分メインコンテンツエリア部分が左にずれる。
     *  その際、画面幅からメインコンテンツエリアの幅を差し引いた余剰分でチャットボットの幅を賄える場合は、ずらす必要がないためズレ幅なしになるが、
     *  余剰分で賄えない場合は左にずらす。
     */
    const mainContainerOffset = ref('')

    const slots = useSlots()
    const hasBreadcrumbHeader = computed(() => !!slots.breadcrumbHeader)
    const hasBreadcrumbFooter = computed(() => !!slots.breadcrumbFooter)
    const hasPageHeading = computed(() => !!slots.pageHeading)
    const hasHero = computed(() => !!slots.hero)
    const hasContentsHeader = computed(() => !!slots.contentsHeader)
    const hasAsidePrimary = computed(() => !!slots.asidePrimary)
    const hasAsideSecondary = computed(() => !!slots.asideSecondary)
    const hasChatbot = computed(() => !!slots.chatbot)

    /**
     * チャットボットの表示条件
     */
    const isChatbotRenderable = computed(() => {
      return hasChatbot.value && chatbotStore.context.isExist && mediaQueryStore.sizeName === 'pc'
    })

    /**
     * secondary aside の sticky ポジション
     */
    const secondaryTopPosition = computed(() => {
      const TOP_GAP_SIZE = 20 as const
      const _top = !props.hasHeader || clientStore.context.isApp
        ? `${TOP_GAP_SIZE}px`
        : !headerStore.context.isShow
          ? `${TOP_GAP_SIZE}px`
          : `${headerStore.context.height + TOP_GAP_SIZE}px`

      return {
        'top': _top,
      }
    })

    watch(() => isChatbotRenderable.value, () => {
      nextTick(() => {
        calcMainContainerOffset()
        calcChatbotHeight()
      })
    })

    watch(() => chatbotStore.context.isOpen, () => {
      calcMainContainerOffset()
    })

    onMounted(() => {
      if (!slots.main) {
        console.error('This page doesn\'t have main contents. Please inject a main slot into App.')
      }

      clientStore.setIsTouchDevice()

      if (import.meta.env.MODE !== 'production') {
        nextTick(warnIfAsideSecondaryIsBlank)
      }

      calcMainContainerOffset()
      calcChatbotHeight()

      /**
       * テンプレート側で共通的に同一ページへのハッシュリンクの制御をイベントリスナに登録する
       * 同一ページ内でのハッシュリンクをクリックした際に、ブラウザでのページ遷移ではなく、スクリプトでのスムーススクロールで遷移させる
       * @description /foo/bar というページを表示中に、#baz または /foo/bar#baz のような同一ページ内ハッシュリンクをクリックした際に、同一ページのため Vue 側の onMounted が発火しない。ハッシュのみのリンク、または同一 origin・pathname・query(search) のリンクではネイティブのアンカーイベントを殺し、Vue 側でスムーススクロールを発火させるイベントをリスナに登録しておく
       */
      registerHashLinkClickHandler({ withInitialScroll: true })
    })

    /**
     * イベントリスナの解除
     */
    onBeforeUnmount(() => {
      window.removeEventListener('resize', resizeListener)
    })

    headerStore.updateShowStatus(props.hasHeader)

    /**
     * secondary aside が「CSS 上は中身あり」と判定されているのに高さが 0 の場合に警告する（開発環境のみ）
     *
     * @description
     *  カラム数は CSS の `:has(> .is-secondary > .aside > :not(:empty))` で決めているため、
     *  aside 直下の子要素が空でなければ 2 カラムとして領域を確保する。
     *  ただし「空のラッパーの中にさらに空のラッパーがある」構造は `:has()` を入れ子にできない制約で CSS から検出できず、
     *  中身が無いのに 2 カラム目が空白のまま残る。その取りこぼしを開発時に気付けるようにする。
     */
    const warnIfAsideSecondaryIsBlank = () => {
      const aside = asideEl.value
      if (!aside) return

      const isJudgedAsHavingContents = !!aside.querySelector(':scope > :not(:empty)')
      if (isJudgedAsHavingContents && aside.clientHeight === 0) {
        console.warn(
          '[TemplateDefault] secondary aside は中身ありと判定されていますが高さが 0 です。'
          + '入れ子になった空のラッパー要素が残っていないか確認してください（CSS の :has() 判定では検出できません）。',
          aside,
        )
      }
    }

    const calcChatbotHeight = () => {
      if (chatbotEl.value) {
        const clientHeight = document.documentElement.clientHeight
        chatbotStore.setHeight(clientHeight - chatbotEl.value.getBoundingClientRect().top)
      }
    }

    /**
     * メインコンテンツエリアの（チャットボット表示による）ずらし幅計算処理
     *
     * @description
     *  チャットボットがレンダリングされる画面では、チャットボットの幅分メインコンテンツエリア部分が左にずれる。
     *  その際、画面幅からメインコンテンツエリアの幅を差し引いた余剰分でチャットボットの幅を賄える場合は、ずらす必要がないためズレ幅なしになるが、
     *  余剰分で賄えない場合は左にずらす。
     */
    const calcMainContainerOffset = () => {
      // チャットボットが存在しない画面ではメインコンテンツエリアをずらさない（ずらし幅: 0）
      if (!isChatbotRenderable.value) {
        mainContainerOffset.value = ''
        return
      }

      const CHATBOT_CLOSED_WIDTH = 200 as const
      const CHATBOT_OPENED_WIDTH = 400 as const
      const CHATBOT_GAP_SIZE = 20 as const
      const GLOBAL_CONTENTS_WIDTH = 1040 as const

      const clientWidth = document.documentElement.clientWidth

      // 画面幅からコンテンツエリア幅を差し引いた余剰エリアの片側分
      const marginSpace = (clientWidth - GLOBAL_CONTENTS_WIDTH) / 2
      const chatbotAreaWidth = chatbotStore.context.isOpen
        ? CHATBOT_OPENED_WIDTH + CHATBOT_GAP_SIZE
        : CHATBOT_CLOSED_WIDTH + CHATBOT_GAP_SIZE

      // 片側余剰エリアでチャットボットの幅分を確保できる場合はずらさない
      if (marginSpace >= chatbotAreaWidth) {
        mainContainerOffset.value = ''
      } else {
        mainContainerOffset.value = `${chatbotAreaWidth}px`
      }
    }

    const resizeListener = throttle(async () => {
      calcChatbotHeight()
      calcMainContainerOffset()
    }, 100)

    window.addEventListener('resize', resizeListener)

    return {
      // store
      screenLockStore,
      userStore,
      clientStore,
      chatbotStore,

      // data
      asideEl,
      chatbotEl,
      mainContainerOffset,

      // computed
      hasBreadcrumbHeader,
      hasBreadcrumbFooter,
      hasPageHeading,
      hasHero,
      hasContentsHeader,
      hasAsidePrimary,
      hasAsideSecondary,
      isChatbotRenderable,
      secondaryTopPosition,
    }
  },
})
</script>

<template>
  <div
    ref="root"
    class="f-container"
    :data-app="clientStore.context.isApp"
    :data-screenlock="screenLockStore.isLock ? 'true' : 'false'"
    :data-backdrop-darken="screenLockStore.isBlackOut"
    :data-backdrop-blur="screenLockStore.isBlur"
    :data-has-global-nav="hasGlobalNavigation">
    <div
      id="global-container"
      class="f-container--body">
      <HeaderGlobal
        v-if="hasHeader && !clientStore.context.isApp"
        :has-global-navigation="hasGlobalNavigation"
        :has-search-form="hasSearchForm"
        :logo-color="logoColor" />

      <div
        class="l-cols__mainGrid"
        :data-app="clientStore.context.isApp"
        :data-has-global-nav="hasGlobalNavigation"
        :data-chatbot="isChatbotRenderable"
        :data-chatbot-opened="chatbotStore.context.isOpen">
        <div
          class="l-cols__mainGrid--col is-primary">
          <section
            :class="[
              'f-section__contents',
              { 'is-slim': isSlim },
            ]"
            :style="{
              '--container-offset': mainContainerOffset,
            }"
            :data-app="clientStore.context.isApp"
            :data-has-aside="hasAsideSecondary"
            :data-container-offset="!!mainContainerOffset">
            <div
              v-if="hasBreadcrumbHeader"
              class="f-section__contents--body is-breadcrumb__header">
              <slot name="breadcrumbHeader" />
            <!-- /.f-section__contents body --></div>

            <div
              v-if="hasPageHeading"
              class="f-section__contents--body is-pageHeading">
              <slot name="pageHeading" />
            <!-- /.f-section__contents body --></div>

            <div
              v-if="hasHero"
              class="f-section__contents--body is-hero">
              <slot name="hero" />
            <!-- /.f-section__contents body --></div>

            <div
              v-if="hasContentsHeader"
              class="f-section__contents--body is-contentsHeader">
              <slot name="contentsHeader" />
            <!-- /.f-section__contents body --></div>

            <div class="f-section__contents--body is-primary">
              <main class="main">
                <slot name="main" />
              </main>

              <aside
                v-if="hasAsidePrimary"
                class="aside">
                <slot name="asidePrimary" />
              </aside>
            <!-- /.f-section__contents body --></div>

            <div
              v-if="hasAsideSecondary"
              class="f-section__contents--body is-secondary"
              :style="secondaryTopPosition">
              <aside
                ref="asideEl"
                class="aside">
                <slot name="asideSecondary" />
              </aside>
            <!-- /.f-section__contents body --></div>

            <div
              v-if="hasBreadcrumbFooter"
              class="f-section__contents--body is-breadcrumb__footer">
              <slot name="breadcrumbFooter" />
            <!-- /.f-section__contents body --></div>
          <!-- /.f-section__contents --></section>

          <TimelineGlobal v-if="hasTimeline && userStore.context.isSignedIn && !clientStore.context.isApp" />
        <!-- /.l-cols__mainGrid col --></div>

        <div
          v-if="isChatbotRenderable"
          class="l-cols__mainGrid--col is-secondary">
          <section
            ref="chatbotEl"
            class="f-section__chatbot"
            :style="{
              height: `${chatbotStore.context.height}px`,
            }"
            :data-chatbot-opened="chatbotStore.context.isOpen">
            <div class="f-section__chatbot--body">
              <slot name="chatbot" />
            <!-- /.f-section__chatbot body --></div>
          <!-- /.f-section__chatbot --></section>
        <!-- /.l-cols__mainGrid col --></div>
      <!-- /.l-cols__mainGrid --></div>

      <template v-if="hasFooter && !clientStore.context.isApp">
        <slot name="footer">
          <FooterGlobal />
        </slot>
      </template>

      <!-- overlay contents -->
      <ScrollToPageTop v-if="hasScrollToPageTop" />
      <LoadingScreen :is-show="screenLockStore.showWaitAnimation" />
      <ToastMessage />
      <MediaQueryObserver />
      <ValidationInspector />
    <!-- /.f-container body --></div>
  <!-- /.f-container --></div>
</template>

<style lang="sass" scoped>
$global-top-gutter-px: 20
.f-container
  $root: &
  width: 100%
  height: 100%
  min-height: 100%

  &::before
    content: ''
    +transition

  &[data-screenlock="true"]
    &::before
      width: 100%
      height: 100%
      display: block
      position: fixed
      top: 0
      left: 0
      z-index: 20

    &[data-backdrop-darken="true"]::before
      background-color: rgba($bg-black, 0.56)

    // スクリーンロック時に「ぼかし」を入れる場合
    &[data-backdrop-blur="true"]::before
      -webkit-backdrop-filter: blur(6px)
      backdrop-filter: blur(6px)

    &:not([data-backdrop-darken="true"])[data-backdrop-blur="true"]::before
      background-color: rgba($bg-white, 0.3)

  & &--body
    min-height: 100%
    display: flex
    flex-direction: column
    padding-top: #{$header-height-pc}px
    background-color: rgba($bg-x-light-gray, 1)

  &[data-app="true"]
    #{$root}--body
      padding-top: 0

  &[data-has-global-nav="false"]
    #{$root}--body
      padding-top: #{$header-primary-height-pc}px

    &[data-app="true"]
      #{$root}--body
        padding-top: 0

  +breakpoint($max-media: tablet)
    #{$root}--body
      padding-top: #{$header-height-sp}px

    &[data-has-global-nav="false"]
      #{$root}--body
        padding-top: #{$header-primary-height-sp}px

.f-section__contents
  $root: &
  $article-width-percent: getRelativeValue($article-width, $global-contents-width-pc)
  $aside-wrapper-width-percent: getRelativeValue(($global-contents-width-pc - $article-width), $global-contents-width-pc) // secondary aside の左マージンを margin ではなく padding で計算する兼ね合いで、aside コンテンツ幅 + マージン量分を幅として持つ
  --container-offset: ''
  width: 100%
  max-width: #{$global-width-pc}px
  display: grid
  grid-template:
    rows: auto auto auto auto auto 1fr auto auto
    columns: minmax($article-width-percent, 1fr) $aside-wrapper-width-percent
  flex-grow: 1
  padding:
    bottom: 1em
    left: #{$global-prop-pc}px
    right: #{$global-prop-pc}px
  +transition((max-width, padding-right)) // チャットボット表示によるコンテンツエリアのずらしのみアニメーションさせる（grid-template-columns を対象に含めるとカラム切替時にメインカラムが伸縮して見える）

  // チャットボットがある画面で、コンテンツエリア外の余剰エリアでチャットボットの幅が賄えない場合（コンテンツエリアの位置ずらしが発生する場合）
  &[data-container-offset="true"]
    max-width: none
    padding-right: calc(var(--container-offset) + #{$global-prop-pc}px)

  & &--body
    width: 100%
    max-width: #{$global-contents-width-pc}px
    justify-self: end

    &.is-breadcrumb__header
      grid:
        row: 1 / 2
        column: 1 / 3
      margin-bottom: 1.2em

    &.is-pageHeading
      grid:
        row: 2 / 3
        column: 1 / 3
      margin-bottom: 1.2em

      &:first-child
        margin-top: 0.5em

    &.is-hero
      grid:
        row: 3 / 4
        column: 1 / 3
      margin-bottom: 2.5em

    &.is-contentsHeader
      grid:
        row: 4 / 5
        column: 1 / 3
      margin-bottom: 1.5em

    &.is-primary
      grid:
        row: 5 / 7
        column: 1 / 2
      margin-bottom: changeSizePx2Em(60)

    &.is-secondary
      $margin-size: $global-contents-width-pc - ($article-width + $aside-width)
      grid:
        row: 5 / 6
        column: 2 / 3
      position: sticky
      top: #{$global-prop-pc}px
      margin-bottom: changeSizePx2Em(40)
      padding-left: getRelativeValue($margin-size, ($aside-width + $margin-size)) // margin-left だと display grid の関係で値がうまく計算されないので、padding で対処
      +transition(top)

      // aside に描画された中身が無い場合は非表示（判定の考え方は .f-section__contents のシングルカラム化ルールを参照）
      &:not(:has(> .aside > :not(:empty)))
        display: none

    &.is-breadcrumb__footer
      grid:
        row: 7 / 8
        column: 1 / 3

  &.is-slim
    max-width: #{$global-width-slim-pc}px

  // secondary aside のスロットが渡されていない画面はシングルカラム
  // * スロットの有無は描画時に同期で確定する値なので、`:has()` 非対応ブラウザ向けのフォールバックも兼ねて常時有効にする
  &[data-has-aside="false"]
    grid-template-columns: 1fr 0

  // secondary aside のスロットはあるが、描画された中身が無い場合もシングルカラム
  // * 「中身が無い」= aside 直下の子要素が全て空（内包コンポーネントが v-if 等で消えている）
  // * `:empty` はコメントノード（Vue が v-if で消した箇所に残す <!--v-if-->）を無視するため、ラッパー要素の種類やクラスに依存せず判定できる
  // * 初回スタイル計算の時点で判定が確定するため、JS でマウント後に切り替える方式で起きていたカラム幅の伸縮（transition）が発生しない
  // * `:has()` 非対応ブラウザではこのルールだけが無効になり、該当画面は 2 カラム（右側が空白）のまま残る
  &:not(:has(> .is-secondary > .aside > :not(:empty)))
    grid-template-columns: 1fr 0

  // アプリ版
  &[data-app="true"]
    #{$root}--body
      &.is-breadcrumb__header
        margin-top: 0

      &.is-primary,
      &.is-secondary
        margin-bottom: 0

  +breakpoint($max-media: tablet)
    #{$root}--body
      &.is-breadcrumb__header
        margin:
          top: -0.2em
          bottom: 1.2em

  +breakpoint
    width: 100%
    display: block
    padding:
      left: 0
      right: 0

    #{$root}--body
      &.is-breadcrumb__footer,
      &.is-pageHeading,
      &.is-contentsHeader,
      &.is-primary,
      &.is-secondary
        padding: 0 #{$global-prop-sp}px

      &.is-contentsHeader
        margin-bottom: 1.5em

      &.is-primary,
      &.is-secondary
        margin-bottom: 0

      &.is-secondary
        position: static
        margin:
          top: changeSizePx2Em($global-prop-sp)
          left: 0
        padding-top: changeSizePx2Em($global-prop-sp)
        border-top: 8px solid $border-x-light-gray

      &.is-breadcrumb__header + .is-hero,
      &.is-hero:first-child
        margin-top: -(changeSizePx2Em($global-top-gutter-px))

      &.is-pageHeading
        margin:
          top: 0.5em
          bottom: 1em

      &.is-breadcrumb__header + .is-pageHeading,
      &.is-pageHeading:first-child
        margin-top: 0

      &.is-breadcrumb__header,
      &.is-breadcrumb__footer
        display: none

    &[data-app="true"]
      #{$root}--body
        &.is-breadcrumb__header
          margin-top: 0

.f-section__chatbot
  max-height: 100%
  padding-bottom: #{$global-prop-pc}px
  +transition

  & &--body
    height: 100%
    margin-right: #{$global-prop-pc}px

  &[data-chatbot-opened="true"]
    $chatbot-area-size: $chatbot-opened-width + $chatbot-gap-size
    width: #{$chatbot-area-size}px
    transform: translateX(-#{$chatbot-area-size}px)  // ラッパーの l-cols__mainGrid--col.is-secondary が width: 0 にして擬似的に fixed のような振る舞いを持たせていてコンテンツ画面外に消えてしまうので、左に座標をずらしてチャットボットが見える状態にする

  &[data-chatbot-opened="false"]
    $chatbot-area-size: $chatbot-closed-width + $chatbot-gap-size
    width: #{$chatbot-area-size}px
    transform: translateX(-#{$chatbot-area-size}px)

.l-cols__mainGrid
  $root: &
  width: 100%
  display: flex
  flex-grow: 1

  & &--col
    display: flex
    flex-direction: column
    padding-top: changeSizePx2Em($global-top-gutter-px)
    +transition

    &.is-primary
      max-width: 100%
      flex-grow: 1
      align-items: center
      margin-right: 0

    &.is-secondary
      width: 0  // sticky でラッパーを基準とした相対的な固定表示をしたいが、primary カラムにチャットボットの幅を意識させたくないため、width は持たせないことで、擬似的に fixed のような振る舞いに見せる
      height: 100%
      position: sticky
      top: 0
      right: 0
      overflow: visible
      z-index: 1

  +breakpoint($max-media: tablet)
    #{$root}--col
      &.is-secondary
        display: none
</style>
